// FILE: client/src/screens/AdminPortfolioScreen.js
import React, { useEffect, useState } from "react";
import {
  Container,
  Table,
  Button,
  Form,
  Row,
  Col,
  Badge,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  listPortfolioAdmin,
  deletePortfolio,
} from "../../actions/portfolioActions";
import {
  getPortfolioSectionLabel,
  PORTFOLIO_DELETE_RESET,
  PORTFOLIO_SECTIONS,
} from "../../constants/portfolioConstants";
import Message from "../../components/Message";
import Loader from "../../components/Loader";

export default function AdminPortfolioScreen({
  initialSection = "production",
  lockedSection = "",
  title = "Admin • Portfolio",
  description = "Manage Production and Bespoke gallery slides.",
  createPath = "/admin/portfolio/new",
  getEditPath = (id) => `/admin/portfolio/${id}`,
}) {
  const dispatch = useDispatch();
  const [section, setSection] = useState(lockedSection || initialSection);
  const isLocked = Boolean(lockedSection);
  const sectionLabel = getPortfolioSectionLabel(section);

  const adminList = useSelector((state) => state.portfolioAdminList);
  const { loading, error, items = [] } = adminList;

  const deleted = useSelector((state) => state.portfolioDelete);
  const { success: successDelete, error: deleteError } = deleted;

  useEffect(() => {
    dispatch(listPortfolioAdmin(section));
  }, [dispatch, section, successDelete]);

  useEffect(() => {
    if (successDelete) {
      dispatch({ type: PORTFOLIO_DELETE_RESET });
    }
  }, [dispatch, successDelete]);

  const deleteHandler = (id) => {
    if (window.confirm("Delete this slide? (soft delete)")) {
      dispatch(deletePortfolio(id));
    }
  };

  return (
    <Container className="py-4">
      <Row className="align-items-center g-3 mb-3">
        <Col>
          <h1>{title}</h1>
          <div className="text-muted">{description}</div>
        </Col>

        <Col md="auto">
          {isLocked ? (
            <Badge bg="dark">{sectionLabel}</Badge>
          ) : (
            <Form.Select
              value={section}
              onChange={(e) => setSection(e.target.value)}
              style={{ minWidth: 220 }}
            >
              {PORTFOLIO_SECTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          )}
        </Col>

        <Col md="auto">
          <Link to={createPath} className="btn btn-dark">
            Create slide
          </Link>
        </Col>
      </Row>

      {deleteError && <Message variant="danger">{deleteError}</Message>}
      {error && <Message variant="danger">{error}</Message>}
      {loading && <Loader />}

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Order</th>
            <th>Title</th>
            <th>Section</th>
            <th>Status</th>
            <th>Edit</th>
            <th>Delete</th>
          </tr>
        </thead>

        <tbody>
          {items.map((it) => (
            <tr
              key={it._id}
              style={it.isDeleted ? { opacity: 0.6 } : undefined}
            >
              <td>{it.order}</td>

              <td>
                {it.title}{" "}
                {it.isDeleted && (
                  <Badge bg="secondary" className="ms-2">
                    Deleted
                  </Badge>
                )}
              </td>

              <td>{it.section}</td>

              <td>
                {it.isPublished ? (
                  <Badge bg="success">Published</Badge>
                ) : (
                  <Badge bg="warning" text="dark">
                    Unpublished
                  </Badge>
                )}
              </td>

              <td>
                <Link to={getEditPath(it._id)}>Edit</Link>
              </td>

              <td>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => deleteHandler(it._id)}
                  disabled={it.isDeleted}
                >
                  Delete
                </Button>
              </td>
            </tr>
          ))}

          {items.length === 0 && (
            <tr>
              <td colSpan={6} className="text-center text-muted">
                No slides found
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </Container>
  );
}
